import { FunctionComponent } from 'react'
import {
  ArrayLocationAPIResponse,
  ProjectSiteSummary,
} from '@black-bear-energy/black-bear-energy-common'
import {
  Box,
  Chip,
  Link,
  TableCell,
  TableRow,
  rucksack,
} from '@black-bear-energy/rucksack'
import { ArrayLocationChip } from '../common/ArrayLocationChip'
import { SiteRFPPhaseChip } from '../common/SiteRFPPhaseChip'
import { TableCellWithDefault } from '../common/TableCellWithDefault'
import { TextWithTooltip } from '../common/TextWithTooltip'
import { getContractTypeLabel } from '../helpers/getContractTypeLabel'
import { getSearchFormattedText } from '../helpers/getSearchFormattedText'
import { handlePhase } from '../helpers/handlePhase'
import { formatSignificantFigures } from 'components/helpers/formatSignificantFigures'

export const SiteRFPRow: FunctionComponent<SiteRFPRowProps> = ({
  site,
  isLastSite,
  search,
}) => {
  const sitePath = `/sites/${site.siteRFPId}`

  return (
    <TableRow
      sx={{
        height: '40px',
        backgroundColor: rucksack.palette.background.page,
        '& .MuiTableCell-root': {
          padding: '3px 16px 3px 32px',
          ...(isLastSite && { borderBottom: 'none' }),
        },
      }}
    >
      {/* empty cell to account for drop down icon */}
      <TableCell />
      <TableCell>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          <Link
            href={sitePath}
            target="_blank"
            rel="noreferrer"
            underline="hover"
            sx={{
              // Override the pink hover and focus color defined in index.css.
              '&:hover, &:focus': {
                color: rucksack.palette.primary.main,
              },
            }}
          >
            <TextWithTooltip
              text={site.siteRFPName}
              variant="body2"
              maxLineCount={1}
              searchString={search}
            />
          </Link>
          {site.section && (
            <Chip
              label={site.section}
              size="mini"
              sx={{ backgroundColor: rucksack.palette.action.selected }}
            />
          )}
        </Box>
      </TableCell>
      <TableCellWithDefault>
        {site.systemSize !== null &&
          formatSignificantFigures(site.systemSize) + ' MW'}
      </TableCellWithDefault>
      <TableCellWithDefault>
        {site.utility && getSearchFormattedText(site.utility, search)}
      </TableCellWithDefault>
      <TableCellWithDefault>
        {site.arrayLocation && (
          <ArrayLocationChip
            arrayLocation={site.arrayLocation as ArrayLocationAPIResponse}
            scale="mini"
          />
        )}
      </TableCellWithDefault>
      <TableCell>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: '4px',
          }}
        >
          {handlePhase(site.phase).map((phase) => (
            <SiteRFPPhaseChip key={phase} phase={phase} scale="mini" />
          ))}
        </Box>
      </TableCell>
      <TableCellWithDefault>
        {getContractTypeLabel(site.contractType)}
      </TableCellWithDefault>
    </TableRow>
  )
}

interface SiteRFPRowProps {
  site: ProjectSiteSummary
  isLastSite?: boolean
  search?: string
}
